import React, { useState, useRef, useEffect, useMemo } from 'react';
import { AttemptStats } from '../types';
import { useTranslation } from '../../../i18n';

interface AttemptFiltersProps {
  status: string;
  search: string;
  stats: AttemptStats;
  loading?: boolean;
  isPlatformAdmin?: boolean;
  isTenantAdmin?: boolean;
  availableTenants?: Array<{ id: string; name: string; slug: string }>;
  availableLearners?: Array<{ id: string; name: string; email: string; role?: string; tenantId?: string }>;
  selectedTenantId?: string;
  selectedUserId?: string;
  onStatusChange: (status: string) => void;
  onSearchChange: (search: string) => void;
  onTenantChange?: (tenantId: string) => void;
  onUserChange?: (userId: string) => void;
}

export const AttemptFilters: React.FC<AttemptFiltersProps> = ({
  status,
  search,
  stats,
  loading = false,
  isPlatformAdmin = false,
  isTenantAdmin = false,
  availableTenants = [],
  availableLearners = [],
  selectedTenantId = 'all',
  selectedUserId = 'all',
  onStatusChange,
  onSearchChange,
  onTenantChange,
  onUserChange,
}) => {
  const { t } = useTranslation();

  const tabs = [
    { key: '', label: t('attempts.tabs.all', undefined, 'All Attempts'), count: stats.total },
    { key: 'IN_PROGRESS', label: t('status.in_progress', undefined, 'In Progress'), count: stats.inProgress },
    { key: 'COMPLETED', label: t('status.completed', undefined, 'Completed'), count: stats.completed },
    { key: 'EXPIRED', label: t('status.expired', undefined, 'Expired'), count: stats.expired },
    { key: 'ABANDONED', label: t('status.abandoned', undefined, 'Abandoned'), count: stats.abandoned },
  ];

  const showAdminControls = isPlatformAdmin || isTenantAdmin;

  // Organization Searchable Dropdown State
  const [isTenantOpen, setIsTenantOpen] = useState(false);
  const [tenantSearchText, setTenantSearchText] = useState('');
  const tenantRef = useRef<HTMLDivElement>(null);

  // Learner Scope Searchable Dropdown State
  const [isLearnerOpen, setIsLearnerOpen] = useState(false);
  const [learnerSearchText, setLearnerSearchText] = useState('');
  const learnerRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tenantRef.current && !tenantRef.current.contains(event.target as Node)) {
        setIsTenantOpen(false);
      }
      if (learnerRef.current && !learnerRef.current.contains(event.target as Node)) {
        setIsLearnerOpen(false);
      }
    }
    if (isTenantOpen || isLearnerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTenantOpen, isLearnerOpen]);

  // Filtered Tenants List
  const filteredTenants = useMemo(() => {
    if (!tenantSearchText.trim()) return availableTenants;
    const q = tenantSearchText.toLowerCase().trim();
    return availableTenants.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.slug?.toLowerCase().includes(q) ||
        t.id?.toLowerCase().includes(q)
    );
  }, [availableTenants, tenantSearchText]);

  // Selected Tenant Display Name
  const selectedTenantName = useMemo(() => {
    if (!selectedTenantId || selectedTenantId === 'all') return t('attempts.all_organizations', undefined, '🏢 All Organizations (Global)');
    const found = availableTenants.find((t) => t.id === selectedTenantId);
    return found ? `${found.name || found.slug} (${found.slug || found.id})` : selectedTenantId;
  }, [availableTenants, selectedTenantId, t]);

  // Filtered Learners List
  const filteredLearners = useMemo(() => {
    if (!learnerSearchText.trim()) return availableLearners;
    const q = learnerSearchText.toLowerCase().trim();
    return availableLearners.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.id?.toLowerCase().includes(q)
    );
  }, [availableLearners, learnerSearchText]);

  // Selected Learner Display Name
  const selectedLearnerName = useMemo(() => {
    if (!selectedUserId || selectedUserId === 'all') return t('attempts.all_learners', undefined, '👥 All Learners');
    if (selectedUserId === 'own') return t('attempts.my_own_attempts', undefined, '👤 My Own Attempts');
    const found = availableLearners.find((u) => u.id === selectedUserId);
    return found ? `👤 ${found.name}` : selectedUserId;
  }, [availableLearners, selectedUserId, t]);

  return (
    <div className="attempt-filters" style={{ display: 'grid', gap: '14px', borderBottom: '1px solid var(--line)', paddingBottom: '16px', maxWidth: '100%', minWidth: 0 }}>
      {/* Upper Control Bar: Admin Scope & Filters */}
      {showAdminControls && (
        <div
          className="attempt-filters__admin-bar"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            borderRadius: '10px',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <div className="attempt-filters__admin-controls" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('attempts.admin_ledger_badge', undefined, '🛡️ Admin Attempt Ledger')}
            </span>

            {/* Searchable Tenant Selector for Super Admin */}
            {isPlatformAdmin && (
              <div ref={tenantRef} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {t('common.organization_label', undefined, 'Organization:')}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsTenantOpen(!isTenantOpen);
                      setIsLearnerOpen(false);
                    }}
                    style={{
                      height: '34px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--line)',
                      background: 'var(--white)',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: 'var(--navy)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      maxWidth: '240px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedTenantName}
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--muted)', marginLeft: 'auto' }}>
                      {isTenantOpen ? '▲' : '▼'}
                    </span>
                  </button>
                </div>

                {isTenantOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      width: '300px',
                      background: '#FFFFFF',
                      borderRadius: '10px',
                      border: '1px solid #CBD5E1',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      padding: '8px',
                    }}
                  >
                    <input
                      type="text"
                      placeholder={t('attempts.search_organization_placeholder', undefined, 'Search organization by name or slug…')}
                      value={tenantSearchText}
                      onChange={(e) => setTenantSearchText(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '7px 10px',
                        borderRadius: '6px',
                        border: '1px solid #CBD5E1',
                        fontSize: '12px',
                        marginBottom: '6px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                      <div
                        onClick={() => {
                          onTenantChange && onTenantChange('all');
                          setIsTenantOpen(false);
                          setTenantSearchText('');
                        }}
                        style={{
                          padding: '7px 10px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12.5px',
                          background: selectedTenantId === 'all' ? '#EFF6FF' : 'transparent',
                          color: selectedTenantId === 'all' ? '#1D4ED8' : '#1E293B',
                          fontWeight: selectedTenantId === 'all' ? 700 : 500,
                        }}
                      >
                        {t('attempts.all_organizations', undefined, '🏢 All Organizations (Global)')}
                      </div>
                      {filteredTenants.length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#94A3B8' }}>
                          {t('attempts.no_organizations_match', { query: tenantSearchText }, `No organizations match "${tenantSearchText}"`)}
                        </div>
                      ) : (
                        filteredTenants.map((tItem) => (
                          <div
                            key={tItem.id}
                            onClick={() => {
                              onTenantChange && onTenantChange(tItem.id);
                              setIsTenantOpen(false);
                              setTenantSearchText('');
                            }}
                            style={{
                              padding: '7px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12.5px',
                              background: selectedTenantId === tItem.id ? '#EFF6FF' : 'transparent',
                              color: selectedTenantId === tItem.id ? '#1D4ED8' : '#1E293B',
                              fontWeight: selectedTenantId === tItem.id ? 700 : 500,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '6px',
                            }}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {tItem.name || tItem.slug}
                            </span>
                            <span style={{ fontSize: '10.5px', color: '#94A3B8', fontFamily: 'monospace' }}>
                              {tItem.slug || tItem.id}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Searchable Learner Selector for Tenant Admin & Super Admin */}
            <div ref={learnerRef} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {t('attempts.view_scope_label', undefined, 'View Scope:')}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setIsLearnerOpen(!isLearnerOpen);
                    setIsTenantOpen(false);
                  }}
                  style={{
                    height: '34px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--line)',
                    background: 'var(--white)',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: 'var(--navy)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    maxWidth: '240px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedLearnerName}
                  </span>
                  <span style={{ fontSize: '10px', color: 'var(--muted)', marginLeft: 'auto' }}>
                    {isLearnerOpen ? '▲' : '▼'}
                  </span>
                </button>
              </div>

              {isLearnerOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    width: '320px',
                    background: '#FFFFFF',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    padding: '8px',
                  }}
                >
                  <input
                    type="text"
                    placeholder={t('attempts.search_learner_placeholder', undefined, 'Search learner by name or email…')}
                    value={learnerSearchText}
                    onChange={(e) => setLearnerSearchText(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '6px',
                      border: '1px solid #CBD5E1',
                      fontSize: '12px',
                      marginBottom: '6px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                    <div
                      onClick={() => {
                        onUserChange && onUserChange('all');
                        setIsLearnerOpen(false);
                        setLearnerSearchText('');
                      }}
                      style={{
                        padding: '7px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12.5px',
                        background: selectedUserId === 'all' ? '#EFF6FF' : 'transparent',
                        color: selectedUserId === 'all' ? '#1D4ED8' : '#1E293B',
                        fontWeight: selectedUserId === 'all' ? 700 : 500,
                      }}
                    >
                      {t('attempts.all_learners', undefined, '👥 All Learners')}
                    </div>
                    <div
                      onClick={() => {
                        onUserChange && onUserChange('own');
                        setIsLearnerOpen(false);
                        setLearnerSearchText('');
                      }}
                      style={{
                        padding: '7px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12.5px',
                        background: selectedUserId === 'own' ? '#EFF6FF' : 'transparent',
                        color: selectedUserId === 'own' ? '#1D4ED8' : '#1E293B',
                        fontWeight: selectedUserId === 'own' ? 700 : 500,
                      }}
                    >
                      {t('attempts.my_own_attempts', undefined, '👤 My Own Attempts')}
                    </div>

                    {filteredLearners.length > 0 && (
                      <div style={{ margin: '6px 0 2px 8px', fontSize: '10.5px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('attempts.learners_in_scope', { count: filteredLearners.length }, `Learners in Scope (${filteredLearners.length})`)}
                      </div>
                    )}

                    {filteredLearners.length === 0 && learnerSearchText ? (
                      <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#94A3B8' }}>
                        {t('attempts.no_learners_match', { query: learnerSearchText }, `No learners match "${learnerSearchText}"`)}
                      </div>
                    ) : (
                      filteredLearners.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => {
                            onUserChange && onUserChange(u.id);
                            setIsLearnerOpen(false);
                            setLearnerSearchText('');
                          }}
                          style={{
                            padding: '7px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            background: selectedUserId === u.id ? '#EFF6FF' : 'transparent',
                            color: selectedUserId === u.id ? '#1D4ED8' : '#1E293B',
                            fontWeight: selectedUserId === u.id ? 700 : 400,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                          }}
                        >
                          <div style={{ fontWeight: 600, color: '#0F172A' }}>{u.name}</div>
                          <div style={{ fontSize: '11px', color: '#64748B', fontFamily: 'monospace' }}>{u.email}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="attempt-filters__stats" style={{ fontSize: '12px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {loading ? (
              <>
                <span className="dashboard-spinner" style={{ width: '12px', height: '12px', borderWidth: '2px', display: 'inline-block' }} />
                <span>{t('attempts.loading_attempts', undefined, 'Loading simulation attempts...')}</span>
              </>
            ) : (
              <span>
                {stats.total === 1
                  ? t('attempts.showing_count', { count: '1' }, 'Showing 1 simulation attempt')
                  : t('attempts.showing_count_plural', { count: stats.total.toLocaleString() }, `Showing ${stats.total.toLocaleString()} simulation attempts`)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main Bar: Status Tabs + Search */}
      <div className="attempt-filters__main-bar" style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '100%', minWidth: 0 }}>
        {/* Scrollable Status Tabs */}
        <div
          className="tabs attempt-filter-tabs attempt-filters__tabs"
          role="tablist"
          style={{ display: 'flex', gap: '6px', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', paddingBottom: '2px', maxWidth: '100%', minWidth: 0 }}
        >
          {tabs.map((tab) => {
            const isActive = status === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onStatusChange(tab.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 14px',
                  borderRadius: '8px',
                  fontSize: '13.5px',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--blue)' : 'var(--muted)',
                  background: isActive ? 'var(--blue-soft)' : 'var(--white)',
                  border: `1px solid ${isActive ? 'var(--blue)' : 'var(--line)'}`,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: '999px',
                    background: isActive ? 'var(--blue)' : 'var(--surface)',
                    color: isActive ? '#fff' : 'var(--muted)',
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="attempt-filters__search" style={{ position: 'relative', width: '100%', minWidth: 0 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('attempts.search_placeholder', undefined, 'Search scenarios...')}
            style={{
              width: '100%',
              height: '38px',
              border: '1px solid var(--line)',
              borderRadius: '8px',
              padding: '0 32px 0 34px',
              background: 'var(--white)',
              fontSize: '13.5px',
              color: 'var(--ink)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <svg
            style={{ position: 'absolute', left: '10px', top: '11px', width: '15px', height: '15px', color: 'var(--faint)' }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '9px',
                border: 0,
                background: 'none',
                color: 'var(--faint)',
                cursor: 'pointer',
                padding: '2px',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
